import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Building2, Plus, UserPlus, Save, Code2, Copy, Eye, Palette, Trash2, Upload, Server, Lock, PenTool, Linkedin, Instagram, Facebook, Clock, Scale, Loader2, FolderOpen, Check, Globe, Send, RefreshCw, Mail, LayoutGrid, CreditCard, FileText, ExternalLink, Receipt, Calendar, Settings2 } from "lucide-react";
import { api, fileUrl, formatApiError } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { useAuth } from "../context/AuthContext";
import { buildSignatureHtml } from "../lib/signature";
import { Avatar } from "../components/Avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABEL = { owner: "Propriétaire", agent: "Agent", member: "Membre", client: "Client" };

export default function CompaniesPage() {
  const { companies, managed, refresh } = useCompany();
  const navigate = useNavigate();
  const [sel, setSel] = useState(null);
  const logoInputRef = useRef();
  const [members, setMembers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newCo, setNewCo] = useState("");
  const [adding, setAdding] = useState(false);
  const [member, setMember] = useState({ email: "", name: "", role: "member", hourly_rate: 0 });

  useEffect(() => { if (!sel && managed[0]) setSel(managed[0]); }, [managed, sel]);
  useEffect(() => { if (sel) { const fresh = companies.find((c) => c.id === sel.id); if (fresh) setSel(fresh); } }, [companies]); // eslint-disable-line
  useEffect(() => { if (sel) loadMembers(sel.id); }, [sel]);

  const loadMembers = async (cid) => {
    const { data } = await api.get(`/companies/${cid}/members`);
    setMembers(data);
  };

  const createCompany = async () => {
    if (!newCo.trim()) return;
    try { await api.post("/companies", { name: newCo }); toast.success("Entreprise créée"); setCreating(false); setNewCo(""); await refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addMember = async () => {
    if (!member.email) { toast.error("Email requis"); return; }
    try { await api.post(`/companies/${sel.id}/members`, member); toast.success("Membre ajouté"); setAdding(false); setMember({ email: "", name: "", role: "member", hourly_rate: 0 }); loadMembers(sel.id); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveRate = async (m, rate, role) => {
    await api.patch(`/companies/${sel.id}/members/${m.id}`, { hourly_rate: Number(rate), role });
    toast.success("Mis à jour"); loadMembers(sel.id);
  };

  const viewAsClient = (m) => navigate(`/portal?as=${m.user_id}&name=${encodeURIComponent(m.name)}`);

  const deleteCompany = async () => {
    if (!window.confirm(`Supprimer définitivement « ${sel.name} » et toutes ses données (tickets, tâches) ?`)) return;
    try {
      await api.delete(`/companies/${sel.id}`);
      toast.success("Entreprise supprimée");
      setSel(null);
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadLogo = async (file) => {
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      await api.patch(`/companies/${sel.id}/profile`, { logo_url: fileUrl(data.id) });
      toast.success("Logo mis à jour");
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="flex h-full bg-background">
      <div className="w-64 border-r border-white/10 bg-card flex flex-col">
        <div className="h-14 flex items-center justify-between px-4 border-b border-white/10">
          <span className="font-cabinet font-bold text-lg">Entreprises</span>
          <button data-testid="new-company-button" onClick={() => setCreating(true)} className="p-2 rounded-md bg-klein text-zinc-950"><Plus size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {companies.map((c) => (
            <button key={c.id} data-testid={`company-${c.id}`} onClick={() => ["owner", "agent"].includes(c.role) && setSel(c)} className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md text-left text-sm ${sel?.id === c.id ? "bg-klein text-zinc-950" : "hover:bg-white/[0.06]"}`}>
              <Building2 size={16} /><span className="flex-1 truncate font-medium">{c.name}</span>
              <span className={`text-[10px] font-mono-label px-1.5 py-0.5 rounded ${sel?.id === c.id ? "bg-card/20" : "bg-white/[0.06] text-zinc-500"}`}>{ROLE_LABEL[c.role]}</span>
            </button>
          ))}
          {companies.length === 0 && <p className="p-6 text-center text-sm text-zinc-400">Aucune entreprise. Créez la première.</p>}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {sel ? (
          <div className="p-6 lg:p-10 max-w-3xl">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <button data-testid="company-logo-upload" onClick={() => logoInputRef.current.click()} title="Changer le logo" className="relative group w-14 h-14 rounded-full overflow-hidden bg-white/[0.06] flex items-center justify-center shrink-0 ring-1 ring-white/10">
                  {sel.logo_url ? <img src={sel.logo_url} alt={sel.name} className="w-full h-full object-cover" /> : <Building2 size={24} className="text-zinc-400" />}
                  <span className="absolute inset-0 bg-black/55 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"><Upload size={18} className="text-white" /></span>
                  <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-klein text-zinc-950 flex items-center justify-center ring-2 ring-card"><Upload size={12} /></span>
                </button>
                <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadLogo(e.target.files[0])} />
                <div>
                  <h1 className="font-cabinet font-bold text-2xl tracking-tight">{sel.name}</h1>
                  <button onClick={() => logoInputRef.current.click()} className="text-sm text-klein hover:underline">{sel.logo_url ? "Changer le logo" : "Ajouter un logo d'entreprise"}</button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {sel.role === "owner" && <button data-testid="add-member-button" onClick={() => setAdding(true)} className="flex items-center gap-1.5 bg-klein text-zinc-950 px-4 py-2 rounded-md text-sm font-medium"><UserPlus size={16} /> Ajouter</button>}
                {sel.role === "owner" && <button data-testid="delete-company-button" onClick={deleteCompany} className="flex items-center gap-1.5 border border-signal/40 text-signal px-3 py-2 rounded-md text-sm font-medium hover:bg-signal/10"><Trash2 size={16} /> Supprimer</button>}
              </div>
            </div>
            <div className="bg-card border border-white/10 rounded-md overflow-hidden">
              <table className="w-full text-left">
                <thead><tr className="border-b border-white/10 bg-background">{["Membre", "Rôle", "Taux horaire (€)", ""].map((h) => <th key={h} className="px-4 py-3 font-mono-label text-[11px] uppercase tracking-wider text-zinc-500">{h}</th>)}</tr></thead>
                <tbody>
                  {members.map((m) => <MemberRow key={m.id} m={m} canEdit={sel.role === "owner"} onSave={saveRate} onViewAs={viewAsClient} />)}
                </tbody>
              </table>
            </div>

            {sel.role === "owner" && <LicenseSection company={sel} onSaved={refresh} />}

            {["owner", "agent"].includes(sel.role) && <ThemeSection company={sel} onSaved={refresh} />}

            {["owner", "agent"].includes(sel.role) && <SignatureBrandingSection company={sel} onSaved={refresh} />}

            {["owner", "agent"].includes(sel.role) && <ClientPortalSection company={sel} />}

            {["owner", "agent"].includes(sel.role) && <SmtpSection company={sel} />}

            {sel.widget_key && <WidgetSection company={sel} />}
          </div>
        ) : <div className="h-full flex items-center justify-center text-zinc-400">Sélectionnez une entreprise</div>}
      </div>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-cabinet">Nouvelle entreprise</DialogTitle></DialogHeader>
          <input data-testid="company-name-input" placeholder="Nom de l'entreprise" value={newCo} onChange={(e) => setNewCo(e.target.value)} className="w-full border border-white/10 rounded-md px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
          <DialogFooter><button onClick={() => setCreating(false)} className="px-4 py-2 rounded-md border border-white/10 text-sm font-medium">Annuler</button><button data-testid="confirm-create-company" onClick={createCompany} className="px-4 py-2 rounded-md bg-klein text-zinc-950 text-sm font-medium">Créer</button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-cabinet">Ajouter un membre</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input data-testid="member-email" placeholder="Email" value={member.email} onChange={(e) => setMember({ ...member, email: e.target.value })} className="w-full border border-white/10 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20" />
            <input placeholder="Nom (optionnel)" value={member.name} onChange={(e) => setMember({ ...member, name: e.target.value })} className="w-full border border-white/10 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20" />
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="member-role" value={member.role} onChange={(e) => setMember({ ...member, role: e.target.value })} className="border border-white/10 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20">
                <option value="agent">Agent</option><option value="member">Membre</option><option value="client">Client</option>
              </select>
              <input data-testid="member-rate" type="number" placeholder="Taux €/h" value={member.hourly_rate} onChange={(e) => setMember({ ...member, hourly_rate: Number(e.target.value) })} className="border border-white/10 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20" />
            </div>
          </div>
          <DialogFooter><button onClick={() => setAdding(false)} className="px-4 py-2 rounded-md border border-white/10 text-sm font-medium">Annuler</button><button data-testid="confirm-add-member" onClick={addMember} className="px-4 py-2 rounded-md bg-klein text-zinc-950 text-sm font-medium">Ajouter</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const PLANS = [
  { key: "standard", label: "STANDARD", color: "#10B981", desc: "Boîte mail multi-entreprises uniquement." },
  { key: "entreprise", label: "ENTREPRISE", color: "#2563EB", desc: "Tout FlowDesk : Tickets, Tâches, Chat, Clients, KB, Rapports…" },
  { key: "premium", label: "PREMIUM", color: "#7C3AED", desc: "ENTREPRISE + Facturation : Devis, Factures, Catalogue, Abonnements & MRR." },
];

function LicenseSection({ company, onSaved }) {
  const [params, setParams] = useSearchParams();
  const [plan, setPlan] = useState(company.plan || "entreprise");
  const [interval, setInterval] = useState("month");
  const [plans, setPlans] = useState([]);
  const [lic, setLic] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState("");
  React.useEffect(() => { setPlan(company.plan || "entreprise"); }, [company.id, company.plan]);

  const loadLicense = React.useCallback(async () => {
    try {
      const [lp, l, inv] = await Promise.all([
        api.get("/license-plans").then((r) => r.data.plans).catch(() => []),
        api.get(`/companies/${company.id}/license`).then((r) => r.data).catch(() => null),
        api.get(`/companies/${company.id}/license/invoices`).then((r) => r.data.invoices).catch(() => []),
      ]);
      setPlans(lp); setLic(l); setInvoices(inv || []);
    } catch {}
  }, [company.id]);
  React.useEffect(() => { loadLicense(); }, [loadLicense]);

  // Retour de Stripe Checkout : activer la licence
  React.useEffect(() => {
    if (params.get("license") === "cancel") { toast.info("Abonnement annulé"); setParams({}, { replace: true }); return; }
    const sid = params.get("session_id");
    if (params.get("license") === "success" && sid) {
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const { data } = await api.get(`/companies/${company.id}/license/status`, { params: { session_id: sid } });
          if (data.payment_status === "paid" || data.status === "complete") {
            toast.success("Abonnement activé — merci !"); await loadLicense(); await onSaved(); setParams({}, { replace: true }); return;
          }
        } catch {}
        if (attempts < 6) setTimeout(poll, 2000);
        else { toast.info("Paiement en cours de confirmation…"); setParams({}, { replace: true }); }
      };
      poll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const priceOf = (key) => {
    const p = plans.find((x) => x.key === key);
    if (!p) return null;
    return interval === "year" ? p.yearly_price : p.monthly_price;
  };

  const subscribe = async (key) => {
    setBusy(key);
    try {
      const { data } = await api.post(`/companies/${company.id}/license/checkout`, { plan: key, interval, origin_url: window.location.origin });
      if (data.url) { window.location.href = data.url; return; }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Abonnement impossible"); }
    finally { setBusy(""); }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post(`/companies/${company.id}/license/portal`, { origin_url: window.location.origin });
      if (data.url) { window.location.href = data.url; return; }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Portail indisponible"); }
    finally { setBusy(""); }
  };

  const status = lic?.license?.status;
  const active = status === "active" || status === "trialing";
  const STATUS_LABEL = { active: ["Actif", "#10B981"], trialing: ["Essai", "#3B82F6"], past_due: ["Échéance dépassée", "#F59E0B"], canceled: ["Résilié", "#EF4444"], none: ["Aucun abonnement", "#71717a"] };
  const [slabel, scolor] = STATUS_LABEL[status] || STATUS_LABEL.none;
  const fmtD = (iso) => iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : "—";

  return (
    <div className="bg-card border border-white/10 rounded-md p-5" data-testid="license-section">
      <div className="flex items-center gap-2 mb-1"><CreditCard size={18} className="text-klein" /><h2 className="font-cabinet font-bold text-lg">Licence &amp; facturation</h2></div>
      <p className="text-zinc-400 text-sm mb-4">Abonnez-vous à FlowDesk pour cette entreprise. Les modules disponibles dépendent de la licence.</p>

      {/* État de l'abonnement actuel */}
      <div className="rounded-xl border border-white/10 bg-background p-4 mb-4 flex flex-wrap items-center gap-4" data-testid="license-status">
        <div>
          <div className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Licence active</div>
          <div className="flex items-center gap-2">
            <span className="font-cabinet font-bold text-lg">{(lic?.plan || plan || "").toUpperCase()}</span>
            <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ background: scolor + "22", color: scolor }} data-testid="license-status-badge">{slabel}</span>
          </div>
        </div>
        <div>
          <div className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-1"><Calendar size={11} /> Souscription</div>
          <div className="text-sm">{fmtD(lic?.license?.started_at)}</div>
        </div>
        <div>
          <div className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-1"><RefreshCw size={11} /> Prochaine échéance</div>
          <div className="text-sm">{fmtD(lic?.license?.current_period_end)}</div>
        </div>
        {active && (
          <button data-testid="license-portal-btn" onClick={openPortal} disabled={busy === "portal"} className="ml-auto flex items-center gap-1.5 h-9 px-3.5 rounded-lg border border-white/15 text-sm font-medium hover:border-klein hover:text-klein disabled:opacity-50">
            {busy === "portal" ? <Loader2 size={15} className="animate-spin" /> : <Settings2 size={15} />} Gérer mon abonnement
          </button>
        )}
      </div>

      {/* Périodicité */}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex gap-1 p-1 bg-white/[0.06] rounded-lg">
          <button data-testid="license-interval-month" onClick={() => setInterval("month")} className={`px-3 py-1.5 rounded-md text-xs font-medium ${interval === "month" ? "bg-card shadow-sm text-foreground" : "text-zinc-500"}`}>Mensuel</button>
          <button data-testid="license-interval-year" onClick={() => setInterval("year")} className={`px-3 py-1.5 rounded-md text-xs font-medium ${interval === "year" ? "bg-card shadow-sm text-foreground" : "text-zinc-500"}`}>Annuel <span className="text-emerald-400">-2 mois</span></button>
        </div>
      </div>

      {/* Plans */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {PLANS.map((p) => {
          const price = priceOf(p.key);
          const current = (lic?.plan || plan) === p.key && active;
          return (
            <div key={p.key} data-testid={`plan-${p.key}`}
              className={`text-left rounded-xl border p-4 transition-all flex flex-col ${current ? "ring-2" : "border-white/10"}`}
              style={current ? { borderColor: p.color, boxShadow: `0 0 0 2px ${p.color}` } : {}}>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: p.color }} />
                <span className="font-cabinet font-bold text-sm">{p.label}</span>
                {current && <Check size={15} className="ml-auto" style={{ color: p.color }} />}
              </div>
              <p className="text-[12px] text-zinc-400 leading-snug flex-1">{p.desc}</p>
              {price != null && (
                <div className="mt-3 mb-3">
                  <span className="font-cabinet font-black text-xl">{price} €</span>
                  <span className="text-xs text-zinc-500"> HT / {interval === "year" ? "an" : "mois"}</span>
                </div>
              )}
              <button data-testid={`subscribe-${p.key}`} disabled={busy === p.key || current} onClick={() => subscribe(p.key)}
                className="w-full h-9 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50"
                style={{ background: current ? "transparent" : p.color, color: current ? scolor : "#fff", border: current ? `1px solid ${p.color}` : "none" }}>
                {busy === p.key ? <Loader2 size={15} className="animate-spin" /> : current ? "Licence actuelle" : <><CreditCard size={14} /> {active ? "Changer pour ce plan" : "S'abonner"}</>}
              </button>
            </div>
          );
        })}
      </div>

      {/* Factures */}
      <div className="mt-5" data-testid="license-invoices">
        <div className="flex items-center gap-2 mb-2"><Receipt size={15} className="text-klein" /><h3 className="font-cabinet font-semibold text-sm">Factures</h3></div>
        {invoices.length === 0 ? (
          <p className="text-sm text-zinc-500">Aucune facture pour l'instant. Elles apparaîtront ici après votre premier paiement.</p>
        ) : (
          <div className="rounded-xl border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-zinc-500 text-xs border-b border-white/10"><th className="px-3 py-2">N°</th><th className="px-3 py-2">Date</th><th className="px-3 py-2">Statut</th><th className="px-3 py-2 text-right">Montant</th><th className="px-3 py-2"></th></tr></thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} data-testid={`invoice-${inv.id}`} className="border-b border-white/[0.06]">
                    <td className="px-3 py-2 font-mono-label text-xs">{inv.number || inv.id.slice(-8)}</td>
                    <td className="px-3 py-2 text-zinc-400">{inv.created ? new Date(inv.created).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</td>
                    <td className="px-3 py-2"><span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${inv.status === "paid" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>{inv.status === "paid" ? "Payée" : inv.status}</span></td>
                    <td className="px-3 py-2 text-right tabular-nums">{inv.amount_paid?.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} {inv.currency}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center gap-2 justify-end">
                        {inv.hosted_invoice_url && <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-klein hover:underline flex items-center gap-1 text-xs" title="Voir"><ExternalLink size={13} /></a>}
                        {inv.invoice_pdf && <a href={inv.invoice_pdf} target="_blank" rel="noreferrer" data-testid={`invoice-pdf-${inv.id}`} className="text-klein hover:underline flex items-center gap-1 text-xs" title="PDF"><FileText size={13} /> PDF</a>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function MemberRow({ m, canEdit, onSave, onViewAs }) {
  const [rate, setRate] = useState(m.hourly_rate || 0);
  const [role, setRole] = useState(m.role);
  const dirty = Number(rate) !== (m.hourly_rate || 0) || role !== m.role;
  return (
    <tr className="border-b border-white/[0.06]">
      <td className="px-4 py-3"><div className="flex items-center gap-2.5"><Avatar name={m.name} color="#27272A" size={32} /><div><div className="font-medium text-sm">{m.name}</div><div className="text-[11px] text-zinc-400">{m.email}</div></div></div></td>
      <td className="px-4 py-3">{canEdit && m.role !== "owner" ? (
        <select value={role} onChange={(e) => setRole(e.target.value)} className="border border-white/10 rounded-md px-2 py-1 text-sm"><option value="agent">Agent</option><option value="member">Membre</option><option value="client">Client</option></select>
      ) : <span className="text-sm">{ROLE_LABEL[m.role]}</span>}</td>
      <td className="px-4 py-3">{canEdit && m.role !== "client" ? <input type="number" value={rate} onChange={(e) => setRate(e.target.value)} className="w-24 border border-white/10 rounded-md px-2 py-1 text-sm" /> : <span className="text-sm font-mono-label">{m.role === "client" ? "—" : `${m.hourly_rate || 0} €`}</span>}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 justify-end">
          {m.role === "client" && <button data-testid={`view-as-client-${m.user_id}`} onClick={() => onViewAs(m)} className="flex items-center gap-1 text-klein text-sm font-medium whitespace-nowrap"><Eye size={14} /> Voir en tant que client</button>}
          {dirty && <button data-testid={`save-member-${m.id}`} onClick={() => onSave(m, rate, role)} className="flex items-center gap-1 text-klein text-sm font-medium"><Save size={14} /> Enregistrer</button>}
        </div>
      </td>
    </tr>
  );
}

const ACCENT_PRESETS = ["#3B82F6", "#10B981", "#A78BFA", "#F59E0B", "#FF6B6B", "#FF3B3B", "#06B6D4", "#EC4899"];

function ThemeSection({ company, onSaved }) {
  const [accent, setAccent] = useState(company.theme?.accent || "#3B82F6");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setAccent(company.theme?.accent || "#3B82F6"); }, [company]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/companies/${company.id}/theme`, { accent });
      toast.success("Thème enregistré — sélectionnez l'entreprise pour l'appliquer");
      await onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div className="mt-8" data-testid="theme-section">
      <div className="flex items-center gap-2 mb-1"><Palette size={18} className="text-klein" /><h2 className="font-cabinet font-bold text-lg tracking-tight">Thème de l'entreprise</h2></div>
      <p className="text-sm text-zinc-500 mb-4">Choisissez la couleur d'accent (liens du header, boutons). Elle s'applique quand cette entreprise est sélectionnée.</p>
      <div className="bg-card border border-white/10 rounded-md p-5 space-y-5">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-400">Couleur d'accent</label>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {ACCENT_PRESETS.map((c) => (
              <button key={c} data-testid={`accent-${c}`} onClick={() => setAccent(c)} className={`w-8 h-8 rounded-full border-2 transition-transform ${accent === c ? "scale-110 border-white" : "border-transparent"}`} style={{ background: c }} />
            ))}
            <input data-testid="accent-picker" type="color" value={accent} onChange={(e) => setAccent(e.target.value)} className="w-8 h-8 rounded-full bg-transparent cursor-pointer" />
          </div>
        </div>
        <div className="flex items-center justify-between pt-1">
          <div className="flex items-center gap-3">
            <span className="text-xs text-zinc-500">Aperçu :</span>
            <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-background border border-white/10">
              <span className="w-3 h-3 rounded-full" style={{ background: accent }} />
              <span className="text-sm font-medium" style={{ color: accent }}>{company.name}</span>
            </div>
          </div>
          <button data-testid="save-theme-button" onClick={save} disabled={saving} className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-klein text-zinc-950 text-sm font-medium"><Save size={15} /> {saving ? "…" : "Enregistrer le thème"}</button>
        </div>
      </div>
    </div>
  );
}

function SignatureBrandingSection({ company, onSaved }) {
  const { user } = useAuth();
  const bannerRef = useRef();
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    setF({
      banner_url: company.banner_url || "", legal_text: company.legal_text || "", hours: company.hours || "",
      social_linkedin: company.social_linkedin || "", social_instagram: company.social_instagram || "",
      social_facebook: company.social_facebook || "", social_x: company.social_x || "",
      website: company.website || "", phone: company.phone || "", address: company.address || "",
      drive_base_url: company.drive_base_url || "",
    });
  }, [company]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const uploadBanner = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      set("banner_url", fileUrl(data.id));
      toast.success("Bannière ajoutée");
    } catch { toast.error("Échec de l'envoi"); } finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try { await api.patch(`/companies/${company.id}/profile`, f); toast.success("Signature enregistrée"); await onSaved?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const preview = buildSignatureHtml(user, { ...company, ...f });
  const field = (label, key, ph, Icon) => (
    <div>
      <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5 mb-1.5">{Icon && <Icon size={11} />}{label}</label>
      <input data-testid={`sig-${key}`} value={f[key] || ""} onChange={(e) => set(key, e.target.value)} placeholder={ph} className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
    </div>
  );

  return (
    <div className="mt-8" data-testid="signature-branding-section">
      <div className="flex items-center gap-2 mb-1"><PenTool size={18} className="text-klein" /><h2 className="font-cabinet font-bold text-lg tracking-tight">Signature enrichie</h2></div>
      <p className="text-sm text-zinc-500 mb-4">Bannière, réseaux sociaux, horaires et mentions légales — ajoutés automatiquement à la signature email de cette entreprise.</p>
      <div className="bg-card border border-white/10 rounded-xl p-5 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {field("Site web", "website", "exemple.com")}
          {field("Téléphone", "phone", "+33 …")}
          {field("Adresse", "address", "12 rue …")}
          {field("Horaires", "hours", "Lun–Ven · 9h–18h", Clock)}
          {field("Dossier Drive racine (projets)", "drive_base_url", "https://drive.google.com/drive/folders/…", FolderOpen)}
          {field("LinkedIn", "social_linkedin", "linkedin.com/company/…", Linkedin)}
          {field("Instagram", "social_instagram", "instagram.com/…", Instagram)}
          {field("Facebook", "social_facebook", "facebook.com/…", Facebook)}
          {field("X (Twitter)", "social_x", "x.com/…")}
        </div>
        <div>
          <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5 mb-1.5"><Scale size={11} /> Mentions légales</label>
          <textarea data-testid="sig-legal_text" rows={2} value={f.legal_text || ""} onChange={(e) => set("legal_text", e.target.value)} placeholder="SIRET, capital social, confidentialité…" className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
        </div>
        <div>
          <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400 mb-1.5 block">Bannière</label>
          <div className="flex items-center gap-3">
            <button data-testid="sig-banner-upload" onClick={() => bannerRef.current.click()} disabled={uploading} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 text-sm hover:bg-white/[0.06]">{uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Importer</button>
            {f.banner_url && <button onClick={() => set("banner_url", "")} className="text-xs text-signal hover:underline">Retirer</button>}
            <input ref={bannerRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadBanner(e.target.files[0])} />
          </div>
        </div>
        <div className="rounded-lg border border-white/10 p-4" style={{ background: "#0d0d12" }}>
          <p className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Aperçu</p>
          <div dangerouslySetInnerHTML={{ __html: preview }} />
        </div>
        <div className="flex justify-end">
          <button data-testid="save-signature-branding" onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-klein text-zinc-950 text-sm font-semibold hover:brightness-110 disabled:opacity-50">{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Enregistrer</button>
        </div>
      </div>
    </div>
  );
}

function SmtpSection({ company }) {
  const [f, setF] = useState({ host: "", port: 465, secure: true, username: "", password: "", from_email: "", from_name: "", has_password: false });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get(`/companies/${company.id}/smtp`).then(({ data }) => setF((p) => ({ ...p, ...data, password: "" }))); }, [company.id]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.host) { toast.error("Hôte SMTP requis"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/companies/${company.id}/smtp`, { host: f.host, port: Number(f.port), secure: f.secure, username: f.username, password: f.password || undefined, from_email: f.from_email, from_name: f.from_name });
      toast.success("Réglages SMTP enregistrés");
      setF((p) => ({ ...p, password: "", has_password: data.has_password }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <div className="mt-8" data-testid="smtp-section">
      <div className="flex items-center gap-2 mb-1"><Server size={18} className="text-klein" /><h2 className="font-cabinet font-bold text-lg tracking-tight">Serveur d'envoi (SMTP)</h2></div>
      <p className="text-sm text-zinc-500 mb-4">Configurez l'envoi sortant en SSL/TLS (port 465). Le mot de passe est chiffré et n'est jamais réaffiché.</p>
      <div className="bg-card border border-white/10 rounded-xl p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400 mb-1.5 block">Hôte SMTP</label>
            <input data-testid="smtp-host" value={f.host} onChange={(e) => set("host", e.target.value)} placeholder="smtp.exemple.com" className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
          </div>
          <div>
            <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400 mb-1.5 block">Port</label>
            <input data-testid="smtp-port" type="number" value={f.port} onChange={(e) => set("port", e.target.value)} className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400 mb-1.5 block">Identifiant</label>
            <input data-testid="smtp-username" value={f.username} onChange={(e) => set("username", e.target.value)} placeholder="contact@exemple.com" className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
          </div>
          <div>
            <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400 mb-1.5 flex items-center gap-1.5"><Lock size={11} /> Mot de passe {f.has_password && <span className="text-emerald-400 normal-case tracking-normal">• enregistré</span>}</label>
            <input data-testid="smtp-password" type="password" value={f.password} onChange={(e) => set("password", e.target.value)} placeholder={f.has_password ? "•••••••• (laisser vide pour conserver)" : "Mot de passe SMTP"} className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400 mb-1.5 block">Email expéditeur</label>
            <input data-testid="smtp-from-email" value={f.from_email} onChange={(e) => set("from_email", e.target.value)} placeholder="ne-pas-repondre@exemple.com" className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
          </div>
          <div>
            <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400 mb-1.5 block">Nom expéditeur</label>
            <input data-testid="smtp-from-name" value={f.from_name} onChange={(e) => set("from_name", e.target.value)} placeholder="Support Exemple" className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
          <input data-testid="smtp-secure" type="checkbox" checked={f.secure} onChange={(e) => set("secure", e.target.checked)} className="accent-klein" /> Connexion sécurisée SSL/TLS (recommandé pour le port 465)
        </label>
        <div className="flex items-center justify-between pt-1">
          <span className="text-[11px] text-zinc-500">L'envoi réel sera activé une fois vos identifiants fournis.</span>
          <button data-testid="save-smtp-button" onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-klein text-zinc-950 text-sm font-semibold hover:brightness-110 disabled:opacity-50">{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Enregistrer</button>
        </div>
      </div>
    </div>
  );
}

function WidgetSection({ company }) {
  const snippet = (mode) => `<script src="${BACKEND}/api/widget.js" data-key="${company.widget_key}" data-mode="${mode}" async></script>`;
  const copy = (mode) => { navigator.clipboard.writeText(snippet(mode)); toast.success("Code copié — collez-le avant </body>"); };
  const preview = (mode) => {
    document.querySelectorAll('script[src*="widget.js"]').forEach((s) => s.remove());
    document.querySelectorAll("#fd-widget-preview-btn, #fd-widget-preview-panel").forEach((e) => e.remove());
    const s = document.createElement("script");
    s.src = `${BACKEND}/api/widget.js`;
    s.setAttribute("data-key", company.widget_key);
    s.setAttribute("data-mode", mode);
    s.async = true;
    document.body.appendChild(s);
    toast.success("Widget chargé en bas à droite 👉");
  };
  return (
    <div className="mt-8" data-testid="widget-section">
      <div className="flex items-center gap-2 mb-1"><Code2 size={18} className="text-klein" /><h2 className="font-cabinet font-bold text-lg tracking-tight">Widgets embarquables</h2></div>
      <p className="text-sm text-zinc-500 mb-4">Collez ce code sur le site de votre client pour afficher un bouton flottant de chat ou de tickets.</p>
      <div className="mb-3 flex items-center gap-2 text-xs"><span className="font-mono-label uppercase tracking-widest text-zinc-400">Clé entreprise</span><code className="font-mono-label bg-white/[0.06] px-2 py-1 rounded text-zinc-300">{company.widget_key}</code></div>
      <div className="grid md:grid-cols-2 gap-4">
        {[["chat", "Widget Chat", "Bouton de discussion en direct"], ["ticket", "Widget Tickets", "Formulaire de demande de support"]].map(([mode, label, desc]) => (
          <div key={mode} className="bg-card border border-white/10 rounded-md p-4">
            <div className="flex items-center justify-between mb-1"><span className="font-semibold text-sm">{label}</span>
              <div className="flex gap-1">
                <button data-testid={`widget-preview-${mode}`} onClick={() => preview(mode)} className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-white/10 hover:border-white/30"><Eye size={13} /> Aperçu</button>
                <button data-testid={`widget-copy-${mode}`} onClick={() => copy(mode)} className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-klein text-zinc-950"><Copy size={13} /> Copier</button>
              </div>
            </div>
            <p className="text-xs text-zinc-400 mb-2">{desc}</p>
            <pre className="bg-zinc-900 text-zinc-100 text-[11px] rounded-md p-3 overflow-x-auto font-mono-label whitespace-pre-wrap break-all">{snippet(mode)}</pre>
          </div>
        ))}
      </div>
    </div>
  );
}

const MODULES = [
  ["tickets", "Tickets"], ["chat", "Discussions"], ["tasks", "Tâches"],
  ["validations", "Demandes de validation"], ["orders", "Boutique & commandes"],
  ["quotes", "Devis"], ["invoices", "Factures"],
];

function ClientPortalSection({ company }) {
  const [b, setB] = useState(null);
  const [invites, setInvites] = useState([]);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [invEmail, setInvEmail] = useState("");
  const [invName, setInvName] = useState("");
  const [inviting, setInviting] = useState(false);

  const load = async () => {
    const [br, iv] = await Promise.all([
      api.get(`/companies/${company.id}/branding`).then((r) => r.data),
      api.get(`/companies/${company.id}/client-invites`).then((r) => r.data).catch(() => []),
    ]);
    setB(br); setInvites(iv);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [company.id]);

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.patch(`/companies/${company.id}/branding`, { slug: b.slug, theme: b.theme, client_modules: b.client_modules }); setB(data); toast.success("Espace client enregistré"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const toggle = (k) => setB({ ...b, client_modules: { ...b.client_modules, [k]: !b.client_modules[k] } });

  const invite = async () => {
    if (!invEmail.trim()) return;
    setInviting(true);
    try {
      const { data } = await api.post(`/companies/${company.id}/client-invites`, { email: invEmail, name: invName });
      toast.success(data.sent ? "Invitation envoyée par email" : "Invitation créée — copiez le lien (SMTP non configuré)");
      if (!data.sent) navigator.clipboard.writeText(data.link).catch(() => {});
      setInvEmail(""); setInvName(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setInviting(false); }
  };
  const resend = async (id) => { const { data } = await api.post(`/client-invites/${id}/resend`); toast.success(data.sent ? "Renvoyée par email" : "Lien copié"); if (!data.sent) navigator.clipboard.writeText(data.link).catch(() => {}); };
  const revoke = async (id) => { await api.delete(`/client-invites/${id}`); load(); };

  if (!b) return null;
  const url = `${BACKEND.replace(/^https?:\/\//, "")}/${b.slug}`;

  return (
    <div className="mt-8" data-testid="client-portal-section">
      <h3 className="font-cabinet font-bold text-lg mb-1 flex items-center gap-2"><Globe size={18} className="text-klein" /> Espace client de marque</h3>
      <p className="text-sm text-zinc-400 mb-4">Une page de connexion à votre image pour vos clients.</p>

      <div className="bg-card border border-white/10 rounded-md p-5 space-y-5">
        <div>
          <label className="block text-[10px] font-medium uppercase tracking-widest text-zinc-400 mb-1">URL de l'espace client</label>
          <div className="flex items-center gap-2">
            <span className="text-sm text-zinc-500 whitespace-nowrap">{BACKEND.replace(/^https?:\/\//, "")}/</span>
            <input data-testid="branding-slug" value={b.slug || ""} onChange={(e) => setB({ ...b, slug: e.target.value })} className="flex-1 bg-background border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20" />
            <button data-testid="branding-copy-url" onClick={() => { navigator.clipboard.writeText(`${BACKEND}/${b.slug}`).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => {}); }} className="p-2 rounded-md hover:bg-white/10 text-zinc-400">{copied ? <Check size={15} className="text-emerald-400" /> : <Copy size={15} />}</button>
          </div>
        </div>

        <div>
          <label className="block text-[10px] font-medium uppercase tracking-widest text-zinc-400 mb-2 flex items-center gap-1.5"><LayoutGrid size={12} /> Modules proposés aux clients</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {MODULES.map(([k, label]) => (
              <button key={k} data-testid={`module-${k}`} onClick={() => toggle(k)} className={`flex items-center justify-between px-3 py-2 rounded-lg border text-sm ${b.client_modules[k] ? "border-klein bg-klein/10 text-klein" : "border-white/10 text-zinc-400"}`}>
                {label}<span className={`w-2 h-2 rounded-full ${b.client_modules[k] ? "bg-klein" : "bg-zinc-600"}`} />
              </button>
            ))}
          </div>
        </div>

        <button data-testid="branding-save" onClick={save} disabled={saving} className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-klein text-zinc-950 text-sm font-medium disabled:opacity-50"><Save size={15} /> {saving ? "…" : "Enregistrer"}</button>
      </div>

      <div className="bg-card border border-white/10 rounded-md p-5 mt-4">
        <h4 className="font-medium text-sm mb-3 flex items-center gap-1.5"><Mail size={15} className="text-klein" /> Inviter des clients</h4>
        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          <input data-testid="invite-email" value={invEmail} onChange={(e) => setInvEmail(e.target.value)} placeholder="email@client.com" className="flex-1 bg-background border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20" />
          <input data-testid="invite-name" value={invName} onChange={(e) => setInvName(e.target.value)} placeholder="Nom (optionnel)" className="sm:w-44 bg-background border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20" />
          <button data-testid="invite-send" onClick={invite} disabled={inviting || !invEmail.trim()} className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-klein text-zinc-950 text-sm font-medium disabled:opacity-50"><Send size={15} /> Inviter</button>
        </div>
        <p className="text-xs text-zinc-500 mb-3">L'invitation est envoyée via le SMTP de l'entreprise. S'il n'est pas configuré, le lien est copié dans votre presse-papiers.</p>
        <div className="space-y-1.5" data-testid="invites-list">
          {invites.map((iv) => (
            <div key={iv.id} data-testid={`invite-${iv.id}`} className="flex items-center justify-between text-sm border-b border-white/[0.06] py-2 last:border-0">
              <div><span className="font-medium">{iv.email}</span> <span className={`text-[11px] ml-2 px-1.5 py-0.5 rounded-full ${iv.status === "accepted" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>{iv.status === "accepted" ? "Actif" : "En attente"}</span></div>
              <div className="flex gap-1">
                {iv.status !== "accepted" && <button data-testid={`invite-resend-${iv.id}`} onClick={() => resend(iv.id)} className="p-1.5 rounded hover:bg-white/10 text-zinc-400" title="Renvoyer / copier le lien"><RefreshCw size={13} /></button>}
                <button data-testid={`invite-revoke-${iv.id}`} onClick={() => revoke(iv.id)} className="p-1.5 rounded hover:bg-white/10 text-signal" title="Supprimer"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
          {invites.length === 0 && <p className="text-xs text-zinc-500 py-2">Aucune invitation pour l'instant.</p>}
        </div>
      </div>
    </div>
  );
}

